import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as projectService from '@/services/projectService';
import { useProjectsStore } from '@/stores';
import { showToast } from '@/components/Toast';
/**
 * useProjects Hook
 * 管理项目列表和当前项目的状态（使用 Zustand store）
 */
export function useProjects() {
  const { t } = useTranslation();
  const {
    setProjects,
    setCurrentProjectId,
    setIsProjectsLoading,
    setHasLoadedProjects,
    setProjectsPromise,
  } = useProjectsStore();
  const getCurrentProject = useProjectsStore(state => state.getCurrentProject);
  /**
   * 加载项目列表
   */
  const loadProjects = useCallback(async () => {
    // 使用 getState() 获取最新状态，避免闭包问题
    const currentState = useProjectsStore.getState();
    // 如果有正在进行的加载 Promise，直接返回它（等待第一次完成）
    if (currentState.projectsPromise) {
      return currentState.projectsPromise;
    }
    setIsProjectsLoading(true);
    // 创建加载 Promise 并存储
    const projectsPromise = (async () => {
      try {
        const data = await projectService.fetchProjects();
        // fetchProjects already returns transformed data with id/name format
        setProjects(data);
        setHasLoadedProjects(true);
      } catch (error) {
        console.error('❌ Failed to load projects:', error);
      } finally {
        setIsProjectsLoading(false);
        setProjectsPromise(null);
      }
    })();
    // 存储 Promise 以便后续调用可以等待
    setProjectsPromise(projectsPromise);
    return projectsPromise;
  }, [
    setProjects,
    setIsProjectsLoading,
    setHasLoadedProjects,
    setProjectsPromise,
  ]);
  /**
   * 创建新项目
   */
  const createProject = useCallback(
    async (projectName: string) => {
      const newProject = await projectService.createProject(projectName);
      // 重新加载项目列表
      await loadProjects();
      return newProject;
    },
    [loadProjects]
  );
  /**
   * 重命名项目
   */
  const renameProject = useCallback(
    async (projectId: string, newName: string) => {
      const res: any = await projectService.renameProject(projectId, newName);
      if (res.code === 200) {
        // 重新加载项目列表
        await loadProjects();
      }
      return res;
    },
    [loadProjects]
  );
  /**
   * 删除项目
   */
  const deleteProject = useCallback(
    async (projectId: string) => {
      const res: any = await projectService.deleteProject(projectId);
      if (res.code === 200) {
        // 重新加载项目列表
        await loadProjects();
      }
      return res;
    },
    [loadProjects]
  );
  /**
   * 选择项目
   */
  const selectProject = useCallback(
    (projectId: string | null) => {
      setCurrentProjectId(projectId);
    },
    [setCurrentProjectId]
  );

  /**
   * Toggle Star 项目
   */
  const toggleStarProject = useCallback(
    async (projectId: string) => {
      const projects = useProjectsStore.getState().projects;
      const project = projects.find(p => p.id === projectId);
      if (project) {
        const newStarred = !project.starred;
        const res = await projectService.toggleStarProject(
          projectId,
          newStarred
        );
        // 🔥 检查后端响应码
        if (res?.code !== 200) {
          showToast(res?.message || t('toast.failedToToggleStar'), 'error', {
            title: t('common.error'),
          });
          return;
        }
        // 重新加载项目列表
        await loadProjects();
      }
    },
    [loadProjects, t]
  );

  return {
    loadProjects,
    createProject,
    renameProject,
    deleteProject,
    selectProject,
    getCurrentProject,
    toggleStarProject,
  };
}
